import React, {useEffect, useState} from 'react';
import styleCart from '../../../PersonalOffice/Cart/Cart.module.css';
import {OneAddressType} from '../../../types/DeliveryData';
import NewInfoCheckbox from '../../../PersonalOffice/MyOrders/MyOrdersPopups/InfoCheckBox/NewInfoCheckbox';
import style from '../../../PersonalOffice/MyOrders/MyOrdersPopups/PopupsCommonStyle.module.css';
import OrderCitySelect from '../../InputSelect/OrderCitySelect';
import OrderTypeDeliverySelect from '../../InputSelect/OrderTypeDeliverySelect';
import OrderAddressSelect from '../../InputSelect/OrderAddressSelect';
import SelectSvgSelector from '../../InputSelect/SelectSvgSelector';
import {Field, useFormikContext} from 'formik';
import LightGreenButton from '../../Buttons/LightGreenButton';
import {useGetAddressesListQuery} from '../../../redux/deliveryApi';
import {deliveryAPI} from '../../../API/api';
import {useTranslation} from 'react-i18next';
import {useDeliveryForm} from '../../../hooks/useDeliveryForm';
import {deliveryStatusTranslate} from '../../../assets/utils/translatersFunc';

const DeliveryAddressesSelector = () => {
	const {t} = useTranslation();
	const {values, setFieldValue} = useFormikContext<any>();
	const serviceName = (values?.service_name || '').toLowerCase();
	const isKyivCourier =
		serviceName.includes('кур') && serviceName.includes('ки');
	const [editDelivery, setEditDelivery] = useState(false);
	const [resetTrigger, setResetTrigger] = useState(0);
	const serviceType: string | undefined = values?.service_type;
	const {data, isFetching, isError} = useGetAddressesListQuery(
		serviceType as string,
		{
			skip: !serviceType,
		},
	);
	const addressesList = data?.addresses ?? [];
	const {handleChangeAddress, handleCreateAddress} = useDeliveryForm();

	// Auto-set Kyiv when courier in Kyiv is selected
	useEffect(() => {
		if (isKyivCourier) {
			// Try to prefill city as Kyiv via NP API
			(async () => {
				try {
					const res = await deliveryAPI.getCityInNP(
						values.service_token,
						'Київ',
					);
					const first = res?.data?.data?.[0]?.Addresses?.find((a: any) =>
						a?.MainDescription?.toLowerCase().includes('київ'),
					);
					if (first) {
						setFieldValue('city_ref', first.DeliveryCity);
						setFieldValue('city_name', first.MainDescription);
					} else {
						// Fallback: set visible name at least
						setFieldValue('city_name', 'Київ');
					}
				} catch (e) {
					setFieldValue('city_name', 'Київ');
				}
			})();
			// Force method to Address for Kyiv courier
			setFieldValue('delivery_type', 'door');
			setFieldValue('delivery_typeForNP', 'getStreet');
		}
	}, [isKyivCourier, values?.service_token, setFieldValue]);

	const handleCreateButton = () => {
		handleCreateAddress();
		setEditDelivery(false);
	};

	// Track delivery_type changes to trigger reset
	const [prevDeliveryType, setPrevDeliveryType] = useState(
		values.delivery_type,
	);

	useEffect(() => {
		if (
			prevDeliveryType !== values.delivery_type &&
			prevDeliveryType !== undefined
		) {
			// Clear only address fields when delivery method changes, keep city
			setFieldValue('address_ref', '');
			setFieldValue('address_name', '');
			setFieldValue('building', '');
			setFieldValue('flat', '');

			// Trigger reset of components
			setResetTrigger((prev) => prev + 1);
		}
		setPrevDeliveryType(values.delivery_type);
	}, [values.delivery_type, prevDeliveryType, setFieldValue]);

	// Автоматический выбор адреса при загрузке данных
	useEffect(() => {
		if (values.address_id && addressesList.length > 0 && !isFetching) {
			const selectedAddress = addressesList.find(
				(address: OneAddressType) => address.id.toString() === values.address_id.toString()
			);
			
			if (selectedAddress) {
				// Обновляем поля адреса, если они отличаются от текущих значений
				if (values.address_name !== selectedAddress.address_name) {
					setFieldValue('address_name', selectedAddress.address_name || '');
				}
				if (values.building !== selectedAddress.building?.toString()) {
					setFieldValue('building', selectedAddress.building || '');
				}
				if (values.flat !== selectedAddress.flat?.toString()) {
					setFieldValue('flat', selectedAddress.flat || '');
				}
				if (values.city_name !== selectedAddress.city_name) {
					setFieldValue('city_name', selectedAddress.city_name || '');
				}
				if (values.address_ref !== selectedAddress.address_name) {
					setFieldValue('address_ref', selectedAddress.address_name || '');
				}
			}
		}
	}, [values.address_id, addressesList, isFetching, setFieldValue, values.address_name, values.building, values.flat, values.city_name, values.address_ref]);

	return (
		<>
			<div className={styleCart.changeAdresInfo}>{t('Адреса доставки')}</div>
			<div className={`${styleCart.adresInfo}`}>
				{isFetching ? (
					<div>{t('Завантаження...')}</div>
				) : addressesList.length > 0 ? (
					<ul>
						{addressesList.map((address: OneAddressType) => {
							// Формируем label в зависимости от типа доставки
							let label: string;
							if (address.service_type === 'self_delivery') {
								// Для самовивоза: country_name, city_name, service_type
								label = `${address.country_name} ${address.city_name} ${deliveryStatusTranslate(address.service_type)}`;
							} else {
								// Для других типов: country_name, city_name, address_name, building, flat
								const buildingFlat = [address.building, address.flat]
									.filter(Boolean)
									.join(', ');
								label = `${address.country_name} ${address.city_name} ${address.address_name}${buildingFlat ? `, ${buildingFlat}` : ''}`;
							}
							return (
								<li
									role="button"
									key={address.id}
									onClick={() => {
										handleChangeAddress(address);
									}}
									onKeyDown={(e) => {
										if (e.key === 'Enter' || e.key === ' ') {
											e.preventDefault();
											handleChangeAddress(address);
										}
									}}
								>
									<NewInfoCheckbox
										labelTxt={label}
										id={address?.id.toString()}
										setFieldValue={setFieldValue}
										values={{
											...values,
											address_id: values.address_id?.toString() || ''
										}}
										valueName={'address_id'}
									/>
								</li>
							);
						})}
					</ul>
				) : (
					<div>{isError ? t('Помилка завантаження') : t('Не знайдено')}</div>
				)}
			</div>
			<div className={styleCart.addNewButton}>
				<button type={'button'} onClick={() => setEditDelivery(!editDelivery)}>
					{!editDelivery ? '+' : '-'} {t('Додати адресу')}
				</button>
			</div>
			{editDelivery && (
				<div className={styleCart.addNew}>
					<div className={styleCart.addNewTitle}>{t('Введіть адресу')}</div>
					<div className={style.editContainer}>
						<div className={style.editColumn}>
							<div className={style.fieldElement}>
								<div className={style.fieldElement_Name}>
									{'Місто доставки'}
								</div>
								<div className={style.fieldElement_Input}>
									{isKyivCourier ? (
										<div
											className={style.input}
											style={{
												display: 'flex',
												alignItems: 'center',
												height: '45px',
												border: '1px solid #c4c4c4',
												borderRadius: '2px',
												padding: '0 12px',
												color: '#999',
												background: '#f8f8f8',
											}}
										>
											{values.city_name || 'Київ'}
										</div>
									) : (
										<OrderCitySelect
											token={values.service_token}
											fieldName={''}
										/>
									)}
								</div>
							</div>
							{values?.service_token?.length > 2 && (
								<div className={style.fieldElement}>
									<div className={style.fieldElement_Name}>
										{t('Спосіб доставки')}
									</div>
									<div className={style.fieldElement_Input}>
										<OrderTypeDeliverySelect
											setFieldValue={setFieldValue}
											currentDeliveryType={values.delivery_type}
										/>
									</div>
								</div>
							)}
							<div className={style.fieldElement}>
								<div className={style.fieldElement_Name}>
									{values.delivery_type === 'door'
										? t('Адреса')
										: values.delivery_type === 'branch'
											? t('Відділення')
											: ''}
								</div>
								<div className={style.fieldElement_Input}>
									<OrderAddressSelect
										token={values.service_token}
										npCityRef={values.city_ref}
										type={values.delivery_typeForNP}
										setFieldValue={setFieldValue}
										resetTrigger={resetTrigger}
										deliveryType={values.delivery_type}
									/>
								</div>
								{values.delivery_type === 'door' && (
									<>
										<div className={style.fieldElement}>
											<div className={style.fieldElement_Name}>Дім</div>
											<div className={style.fieldElement_Input}>
												<span className={style.fieldIcon}>
													<SelectSvgSelector id={'nameIcon'} />
												</span>
												<Field
													className={style.input}
													placeholder={'Дім'}
													value={values.building}
													name={'building'}
												/>
											</div>
										</div>
										<div className={style.fieldElement}>
											<div className={style.fieldElement_Name}>Квартира</div>
											<div className={style.fieldElement_Input}>
												<span className={style.fieldIcon}>
													<SelectSvgSelector id={'nameIcon'} />
												</span>
												<Field
													className={style.input}
													placeholder={'Квартира'}
													name={'flat'}
													value={values.flat}
												/>
											</div>
										</div>
									</>
								)}
								<div className={styleCart.submitButtonWrapper}>
									<LightGreenButton
										type={'button'}
										onClick={handleCreateButton}
										text={t('Продовжити')}
									/>
								</div>
							</div>
						</div>
					</div>
				</div>
			)}
		</>
	);
};

export default DeliveryAddressesSelector;
